import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select } from "../components/UI";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, Loader2, Users, Copy, MapPin, CheckCircle2, QrCode, Clock, UserPlus } from "lucide-react";

const APP = window.location.origin;

export default function EventsPage() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", event_type: "fundraiser", date: "", time: "", location: "", capacity: "", fundraising_goal: "", description: "" });
  const [rsvpView, setRsvpView] = useState(null);
  const [shiftsView, setShiftsView] = useState(null);

  const load = () => api.get("/political-os/events").then(r => setData(r.data)).catch(() => setData({ events: [] }));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.date) return toast.error("Name and date are required");
    try {
      await api.post("/political-os/events", { ...form, capacity: form.capacity ? parseInt(form.capacity) : null, fundraising_goal: form.fundraising_goal ? parseFloat(form.fundraising_goal) : null });
      toast.success("Event published — RSVP page is live");
      setForm({ ...form, name: "", date: "", location: "" });
      load();
    } catch (e) { toast.error(errMsg(e, "Could not create event")); }
  };
  const remove = async (id) => { try { await api.delete(`/political-os/events/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const openRsvps = async (ev) => {
    const { data: d } = await api.get(`/political-os/events/${ev.id}/rsvps`);
    setRsvpView(d);
  };
  const checkin = async (evid, rid) => {
    await api.post(`/political-os/events/${evid}/rsvps/${rid}/checkin`);
    toast.success("Checked in");
    openRsvps({ id: evid });
  };
  const copyLink = (slug) => {
    navigator.clipboard?.writeText(`${APP}/e/${slug}`).then(() => toast.success("RSVP link copied")).catch(() => {});
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="events-page">
      <PageHeader eyebrow="Operations" title="Events"
        subtitle="Rallies, fundraisers, town halls — each event gets a public RSVP page and a check-in list." />
      <Card data-testid="create-event-card">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Input label="Event name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Fall Fundraising Reception" className="col-span-2" data-testid="event-name-input" />
          <Select label="Type" value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
            {["rally", "fundraiser", "town_hall", "meet_greet", "volunteer_event", "debate_watch", "campaign_launch", "press_event", "virtual_event"].map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </Select>
          <Input label="Date" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="event-date-input" />
          <Input label="Time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} placeholder="7:00 PM" />
          <Input label="Location" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Venue, city" className="col-span-2" />
          <Input label="Capacity" type="number" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} placeholder="150" />
        </div>
        <div className="mt-3"><Btn onClick={create} data-testid="create-event-btn"><Plus className="w-4 h-4" /> Publish event</Btn></div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.events.map(ev => (
          <Card key={ev.id} data-testid={`event-card-${ev.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display text-lg font-bold text-white truncate">{ev.name}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                  <span className="uppercase tracking-wider text-cyan-300">{(ev.event_type || "").replace(/_/g, " ")}</span>
                  <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{ev.date}{ev.time ? ` · ${ev.time}` : ""}</span>
                  {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                </div>
              </div>
              <button onClick={() => remove(ev.id)} className="text-slate-600 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-cyan-300/70" /> {ev.rsvp_count} RSVP{ev.rsvp_count === 1 ? "" : "s"}{ev.capacity ? ` / ${ev.capacity}` : ""}</span>
              <span>{ev.checked_in} checked in</span>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => copyLink(ev.slug)} data-testid={`copy-event-link-${ev.id}`}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:border-cyan-400/40 transition-colors inline-flex items-center gap-1.5"><Copy className="w-3 h-3" /> Copy RSVP link</button>
              <a href={`/e/${ev.slug}`} target="_blank" rel="noreferrer"
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:border-cyan-400/40 transition-colors">Preview page</a>
              <button onClick={() => openRsvps(ev)} data-testid={`view-rsvps-${ev.id}`}
                className="text-xs px-3 py-1.5 rounded-full border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition-colors">Guest list</button>
              <button onClick={() => setShiftsView(ev)} data-testid={`view-shifts-${ev.id}`}
                className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:border-emerald-400/40 hover:text-emerald-300 transition-colors inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Shifts</button>
            </div>
          </Card>
        ))}
        {data.events.length === 0 && <Card><div className="text-sm text-slate-500 py-4 text-center">No events yet — publish your first above.</div></Card>}
      </div>

      {shiftsView && <ShiftsModal event={shiftsView} onClose={() => setShiftsView(null)} />}

      {rsvpView && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setRsvpView(null)}>
          <div className="bg-[#0d1322] border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="rsvp-modal">
            <div className="px-5 py-3 border-b border-white/10 font-display font-bold">{rsvpView.event?.name || "Guest list"} — {rsvpView.rsvps.length} guests</div>
            <div className="divide-y divide-white/5 overflow-y-auto">
              {rsvpView.rsvps.map(r => (
                <div key={r.id} className="px-5 py-3 flex items-center gap-3" data-testid={`rsvp-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white">{r.name}{r.guests ? ` +${r.guests}` : ""}</div>
                    <div className="text-[11px] text-slate-500">{r.email}</div>
                  </div>
                  {r.checked_in ? (
                    <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> checked in</span>
                  ) : (
                    <button onClick={() => checkin(rsvpView.event.id, r.id)} data-testid={`checkin-${r.id}`}
                      className="text-xs px-3 py-1 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors">Check in</button>
                  )}
                </div>
              ))}
              {rsvpView.rsvps.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">No RSVPs yet — share the link.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------- Shifts modal -------------------

function ShiftsModal({ event, onClose }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: "", starts_at: "", ends_at: "", capacity: 5, notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/political-os/events/${event.id}/shifts`);
      setData(r.data);
    } catch (e) { toast.error(errMsg(e, "Could not load shifts")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const create = async () => {
    if (!form.title.trim() || !form.starts_at || !form.ends_at) return toast.error("Title, start and end are required");
    setBusy(true);
    try {
      await api.post(`/political-os/events/${event.id}/shifts`, {
        title: form.title, starts_at: form.starts_at, ends_at: form.ends_at,
        capacity: parseInt(form.capacity) || 5, notes: form.notes });
      toast.success("Shift published");
      setForm({ title: "", starts_at: "", ends_at: "", capacity: 5, notes: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (sid) => {
    if (!window.confirm("Delete this shift and remove all its volunteers?")) return;
    try { await api.delete(`/political-os/events/${event.id}/shifts/${sid}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const unassign = async (sid, email) => {
    try { await api.post(`/political-os/shifts/${sid}/unassign?email=${encodeURIComponent(email)}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0d1322] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="shifts-modal">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Volunteer shifts</div>
            <div className="font-display font-bold text-white">{event.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white text-sm">✕</button>
        </div>
        <div className="p-5 border-b border-white/5 grid grid-cols-2 gap-3">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Shift title (e.g. Morning canvass)" data-testid="shift-title" />
          <Input type="number" min={1} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="Capacity" data-testid="shift-capacity" />
          <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="shift-starts-at" />
          <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="shift-ends-at" />
          <Input className="col-span-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What volunteers should know (address, what to bring…)" data-testid="shift-notes" />
          <Btn onClick={create} disabled={busy} data-testid="shift-create-btn" className="col-span-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Publish shift
          </Btn>
        </div>
        <div className="overflow-y-auto flex-1" data-testid="shifts-list">
          {!data && <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-300" /></div>}
          {data && data.shifts.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No shifts yet — add the first one above. Supporters will see them on the event&apos;s public page.</div>}
          {data && data.shifts.map((s) => (
            <div key={s.id} className="px-5 py-3 border-b border-white/5" data-testid={`shift-row-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium">{s.title}</div>
                  <div className="text-[11px] text-slate-500">{(s.starts_at || "").slice(0, 16).replace("T", " ")} → {(s.ends_at || "").slice(11, 16)}</div>
                  {s.notes && <div className="text-[11px] text-slate-400 mt-0.5">{s.notes}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs"><span className="text-white font-bold">{s.filled_count}</span><span className="text-slate-500">/{s.capacity}</span></div>
                  <button onClick={() => remove(s.id)} data-testid={`shift-remove-${s.id}`} className="text-[10px] text-rose-300 hover:underline mt-0.5">Delete</button>
                </div>
              </div>
              {(s.assignments || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`shift-assignments-${s.id}`}>
                  {s.assignments.map((a) => (
                    <span key={a.id} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-200 inline-flex items-center gap-1" data-testid={`shift-vol-${a.email}`}>
                      <UserPlus className="w-2.5 h-2.5" /> {a.name}
                      <button onClick={() => unassign(s.id, a.email)} className="text-slate-500 hover:text-rose-300 ml-0.5">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

